// ConciergeCarousel: 5-step introduction the first time an operator
// signs in. Uses the Live Golden Core (AtomCore) as the sole brand mark
// and V2 True Glass panels (16px blur) for each slide. Dismissible from
// any step and persisted via POST /onboarding/concierge/dismiss so the
// operator never sees it twice.
import { useCallback, useEffect, useMemo, useState } from "react";
import { ChefHat, Sparkles, TrendingUp, Utensils, Waypoints, ArrowRight, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import AtomCore from "@/components/AtomCore";

// --- Slide illustrations (inline SVG, no assets) --------------------------

function FlowDiagram() {
  return (
    <svg viewBox="0 0 400 180" className="w-full h-auto" aria-hidden="true">
      <defs>
        <linearGradient id="flow-gold" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#e8cf9a" />
          <stop offset="1" stopColor="#c8a96a" />
        </linearGradient>
      </defs>
      {["Ingredients", "Recipes", "Cost", "Margin"].map((label, i) => (
        <g key={label} transform={`translate(${20 + i * 100} 60)`}>
          <rect width="82" height="60" rx="12" fill="rgba(255,255,255,0.05)" stroke="url(#flow-gold)" strokeWidth="1.4" />
          <text x="41" y="34" textAnchor="middle" fill="#e8cf9a" fontFamily="ui-sans-serif, system-ui" fontSize="13" fontWeight="600">{label}</text>
        </g>
      ))}
      {[0, 1, 2].map((i) => (
        <g key={i} transform={`translate(${102 + i * 100} 90)`}>
          <line x1="0" y1="0" x2="18" y2="0" stroke="url(#flow-gold)" strokeWidth="1.6" />
          <path d="M18 0 L12 -4 L12 4 Z" fill="url(#flow-gold)" />
        </g>
      ))}
    </svg>
  );
}

function DataDiagram() {
  return (
    <svg viewBox="0 0 400 180" className="w-full h-auto" aria-hidden="true">
      <defs>
        <linearGradient id="dd-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="rgba(232,207,154,0.9)" />
          <stop offset="1" stopColor="rgba(200,169,106,0.3)" />
        </linearGradient>
      </defs>
      <rect x="15" y="20" width="370" height="140" rx="14" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.08)" />
      <text x="200" y="42" textAnchor="middle" fill="#c8a96a" fontFamily="ui-sans-serif" fontSize="11" letterSpacing="4">SYNC ENGINE</text>
      {[36, 78, 60, 92, 70, 110, 88, 55, 74].map((h, i) => (
        <rect key={i} x={40 + i * 38} y={150 - h} width="26" height={h} rx="4" fill="url(#dd-fill)" />
      ))}
      <line x1="40" y1="150" x2="380" y2="150" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
    </svg>
  );
}

function ChefDiagram() {
  return (
    <svg viewBox="0 0 400 180" className="w-full h-auto" aria-hidden="true">
      <defs>
        <linearGradient id="cd-gold" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="#e8cf9a" />
          <stop offset="1" stopColor="#7a5a1e" />
        </linearGradient>
      </defs>
      <circle cx="200" cy="90" r="60" fill="rgba(232,207,154,0.06)" stroke="url(#cd-gold)" strokeWidth="1.5" />
      <circle cx="200" cy="90" r="42" fill="none" stroke="url(#cd-gold)" strokeWidth="0.8" strokeDasharray="4 4" />
      <text x="200" y="86" textAnchor="middle" fill="#e8cf9a" fontFamily="ui-sans-serif" fontSize="13" fontWeight="600">Digital</text>
      <text x="200" y="102" textAnchor="middle" fill="#e8cf9a" fontFamily="ui-sans-serif" fontSize="13" fontWeight="600">Corporate Chef</text>
      {[45, 130, 260, 340].map((angle, i) => {
        const rad = (angle * Math.PI) / 180;
        const x = 200 + Math.cos(rad) * 100;
        const y = 90 + Math.sin(rad) * 60;
        return (
          <g key={i}>
            <line x1={200 + Math.cos(rad) * 62} y1={90 + Math.sin(rad) * 62} x2={x - Math.cos(rad) * 4} y2={y - Math.sin(rad) * 4} stroke="url(#cd-gold)" strokeWidth="0.6" />
            <circle cx={x} cy={y} r="4.5" fill="url(#cd-gold)" />
          </g>
        );
      })}
    </svg>
  );
}

// --- Slide content --------------------------------------------------------

const SLIDES = [
  {
    icon: Sparkles,
    eyebrow: "Welcome",
    title: "Good evening, Chef.",
    body: "Delighted to have you in the kitchen. Give me 90 seconds and I will show you exactly how tonight's service, and every service after it, gets a little more profitable, a little more elegant.",
    illustration: null,
  },
  {
    icon: Utensils,
    eyebrow: "The chain",
    title: "Every plate has a story.",
    body: "Your pantry, your recipes, your supplier invoices, your final plate margin. All wired to the same source of truth. When a supplier price shifts on Tuesday, every recipe that touches it reprices before Wednesday's mise en place.",
    illustration: <FlowDiagram />,
  },
  {
    icon: Waypoints,
    eyebrow: "Sync Engine",
    title: "Your suppliers, no more spreadsheets.",
    body: "Wire PurchasePlus, MarketMan, or Fourth in one click. Invoices land automatically, every SKU finds its place in your pantry, and your food cost stays true for the next seven days. Retire the clipboard forever.",
    illustration: <DataDiagram />,
  },
  {
    icon: ChefHat,
    eyebrow: "Digital Corporate Chef",
    title: "Your private culinary strategist.",
    body: "Ask about margin, yield, supplier drift, or how last night stacked against last week. I read every batch, every invoice, every shift in your kitchen and answer in your voice, in seconds.",
    illustration: <ChefDiagram />,
  },
  {
    icon: TrendingUp,
    eyebrow: "Your first plate",
    title: "Show me your signature dish.",
    body: "Head to Recipes, drop in one plate you are serving tonight. Under a minute, I will tell you your food cost percent, your true margin, and where to price it on the menu. Consider it my welcome gift.",
    illustration: null,
  },
];

// --- Component ------------------------------------------------------------

const STORAGE_KEY = "concierge_dismissed_local";

export default function ConciergeCarousel() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [idx, setIdx] = useState(0);
  const [dismissing, setDismissing] = useState(false);

  const total = SLIDES.length;
  const slide = SLIDES[idx];

  const dismiss = useCallback(async () => {
    setDismissing(true);
    try {
      await api.post("/onboarding/concierge/dismiss");
    } catch {
      // fall through: hide client side either way
    }
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch {}
    setVisible(false);
    setDismissing(false);
  }, []);

  useEffect(() => {
    if (!user) return;
    // Never re-show if the operator dismissed on this device this session.
    try {
      if (localStorage.getItem(STORAGE_KEY) === "1") return;
    } catch {}
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/onboarding/concierge/status");
        if (!cancelled && data?.should_show) setVisible(true);
      } catch {
        // Fail closed: never show if the server call errors, so we do
        // not annoy the operator on a bad network.
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const goNext = () => {
    if (idx === total - 1) dismiss();
    else setIdx((i) => Math.min(total - 1, i + 1));
  };
  const goBack = () => setIdx((i) => Math.max(0, i - 1));

  const dots = useMemo(() => Array.from({ length: total }), [total]);

  if (!visible) return null;
  const Icon = slide.icon;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center px-4 py-6"
      style={{
        background: "rgba(4,3,2,0.62)",
        backdropFilter: "blur(16px) saturate(180%)",
        WebkitBackdropFilter: "blur(16px) saturate(180%)",
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Culinaire onboarding"
      data-testid="concierge-carousel"
    >
      <div
        className="relative w-full max-w-4xl rounded-3xl overflow-hidden"
        style={{
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.035) 55%, rgba(255,255,255,0.015) 100%)",
          backdropFilter: "blur(16px) saturate(200%)",
          WebkitBackdropFilter: "blur(16px) saturate(200%)",
          border: "1px solid rgba(212,175,55,0.45)",
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.28), 0 0 30px #d4af3730, 0 50px 120px rgba(0,0,0,0.6)",
        }}
      >
        {/* Prism hairline */}
        <div
          aria-hidden
          className="absolute top-0 left-8 right-8 h-px rounded-full pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, transparent, #D4AF37, #FFF8E1, #D4AF37, transparent)",
          }}
        />
        {/* Dismiss control */}
        <button
          type="button"
          onClick={dismiss}
          disabled={dismissing}
          className="absolute top-4 right-4 text-white/50 hover:text-white/90 transition"
          aria-label="Dismiss"
          data-testid="concierge-dismiss"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_1.2fr] gap-0">
          {/* Left: Live Golden Core + eyebrow + step count */}
          <div className="relative p-8 md:p-10" style={{
            background: "linear-gradient(180deg, rgba(20,15,8,0.55), rgba(8,6,4,0.9))",
          }}>
            <div className="flex items-center gap-3">
              <Icon className="w-4 h-4" style={{ color: "#D4AF37" }} />
              <div className="label-eyebrow" style={{ color: "#D4AF37" }}>{slide.eyebrow}</div>
            </div>
            <div className="mt-8 relative flex items-center justify-center" style={{ minHeight: "180px" }}>
              <div
                className="rounded-full flex items-center justify-center"
                style={{
                  width: 180,
                  height: 180,
                  background:
                    "radial-gradient(circle at 30% 30%, rgba(255,248,225,0.15), rgba(212,175,55,0.05) 60%, transparent 100%)",
                  border: "1px solid rgba(212,175,55,0.45)",
                  boxShadow: "0 0 40px #d4af3760, inset 0 2px 0 rgba(255,255,255,0.15)",
                }}
              >
                <AtomCore size={140} testId="concierge-atom" />
              </div>
              <div
                className="absolute -top-2 -right-4 rounded-full px-3 py-1 text-[11px] tabular"
                style={{
                  background: "rgba(0,0,0,0.6)",
                  border: "1px solid rgba(212,175,55,0.35)",
                  color: "#f5e6b0",
                }}
                data-testid="concierge-step-count"
              >
                {idx + 1} of {total}
              </div>
            </div>
            <div className="mt-8">
              <div
                className="font-display text-lg"
                style={{ color: "#D4AF37", textShadow: "0 0 14px rgba(212,175,55,0.5)" }}
              >
                Your Digital Corporate Chef
              </div>
              <p className="text-[12px] text-white/55 mt-1 max-w-[16rem] leading-relaxed">
                Here to walk you through the platform. Dismiss anytime. Reopen from Settings.
              </p>
            </div>
          </div>

          {/* Right: content */}
          <div className="p-8 md:p-10 space-y-6 min-h-[420px] flex flex-col" data-testid={`concierge-slide-${idx}`}>
            <h2 className="font-display text-3xl md:text-4xl leading-tight tracking-tight">
              {slide.title}
            </h2>
            <p className="text-white/70 text-base leading-relaxed">
              {slide.body}
            </p>
            {slide.illustration && (
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                {slide.illustration}
              </div>
            )}

            <div className="flex-1" />

            <div className="flex items-center justify-between pt-4">
              <div className="flex gap-1.5" aria-label="progress dots">
                {dots.map((_, i) => (
                  <span
                    key={i}
                    className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-amber-200" : "w-2 bg-white/20"}`}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={dismiss}
                  disabled={dismissing}
                  className="text-xs text-white/60 hover:text-white/90 px-2 py-1"
                  data-testid="concierge-skip"
                >
                  Skip
                </button>
                {idx > 0 && (
                  <Button variant="outline" size="sm" onClick={goBack} data-testid="concierge-back">
                    Back
                  </Button>
                )}
                <Button size="sm" onClick={goNext} className="gap-1.5" data-testid="concierge-next">
                  {idx === total - 1 ? "Take me in" : "Next"}
                  <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
